import InputFilter from 'volta/components/filters/input-filter';
// @ts-ignore
import Service, { service } from '@ember/service';
import { columnsToProps, columnsToSortOptions } from 'volta/models/collection-view';
import { buildCustomFieldColumn, parseCFApiParamKey } from 'volta/models/custom-field-definition';
import { SourceResourceName } from 'volta/models/source';

import type RouterService from '@ember/routing/router-service';
import type { IntlService } from 'ember-intl';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type {
  ICollectionDefinitionBuilder,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICreateCollectionViewCmd
} from 'volta/models/types/collection-view';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type LabelService from 'volta/services/label-service';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type Source from 'volta/models/source';

export default class SourcesCollectionDefinitionBuilder
  extends Service
  implements ICollectionDefinitionBuilder
{
  @service store!: StoreService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service sessionUser!: SessionUserService;
  @service router!: RouterService;

  buildColumns() {
    const { intl, customFieldDefinitionsService } = this;
    const defs: IBvTableColumn[] = [
      {
        columnName: intl.t('code'),
        sortKey: 'code',
        valuePath: 'code',
        minWidth: 70,
        isFixed: 'left'
      },
      {
        columnName: intl.t('description'),
        sortKey: 'description',
        valuePath: 'description',
        minWidth: 80,
        isFixed: 'left',
        classNames: 'u-color-text-subdued'
      }
    ];

    const customFieldProps: (IBvTableColumn | undefined)[] = (
      customFieldDefinitionsService.sourcesCustomProperties() ?? []
    ).map((def) =>
      buildCustomFieldColumn(def, (model: Source) => {
        const dataType = def.dataType ? def.dataType.toLowerCase() : undefined;
        const { customFields } = model;
        const key = parseCFApiParamKey(def.property);

        const value = customFields ? customFields[key] : undefined;
        return {
          value,
          dataType
        };
      })
    );

    return defs.concat(customFieldProps.filter(Boolean) as IBvTableColumn[]);
  }

  buildFilters(_queryParamsValues: TQueryParams) {
    const { customFieldDefinitionsService } = this;

    const normalDefs: TFilterDefinition[] = [
      {
        queryParam: 'q',
        apiParam: 'q',
        comparators: ['lk'],
        // @ts-ignore
        component: InputFilter,
        hidden: true,
        componentArgs: {}
      }
    ];

    return normalDefs.concat(customFieldDefinitionsService.sourceCustomFieldsFilters());
  }

  buildDefaultView(columns: IBvTableColumn[]): ICreateCollectionViewCmd {
    const { intl } = this;
    const properties = columnsToProps(columns, (c: IBvTableColumn) => !c.disabled);
    return {
      name: intl.t('collectionViewsPage.default.name', {
        resourceNamePlural: intl.t(SourceResourceName.plural)
      }),
      description: intl.t('collectionViewsPage.default.description', {
        resourceNamePlural: intl.t(SourceResourceName.plural)
      }),
      collectionType: 'TABLE',
      entityType: SourceResourceName.plural,
      properties,
      sortBy: [{ property: 'code', asc: true }],
      createdBy: this.sessionUser.userId,
      filters: []
    };
  }

  build(queryParams: TQueryParams, sorts: ICollectionSortOption[] = []) {
    const filterDefinitions = this.buildFilters(queryParams);
    const columns = this.buildColumns();
    const defaultView = this.buildDefaultView(columns);

    const view: ICollectionViewDefinition = {
      resourceName: SourceResourceName,
      sortLimit: 0,
      sorts: columnsToSortOptions(columns).concat(sorts) as ICollectionSortOption[],
      properties: columnsToProps(columns),
      filters: filterDefinitions,
      definitions: columns,
      types: ['TABLE'],
      defaultView
    };

    return view;
  }
}

declare module '@ember/service' {
  interface Registry {
    'collection-builder-sources': SourcesCollectionDefinitionBuilder;
  }
}
